import {
  ComponentInCsm,
  CompRef,
  DocumentServicesObject,
  EditorClientSpecMapEntry,
} from '@wix/document-services-types';
import { EditorPointerServiceAPI } from '@wix/editor-document-logic-api';
import { EventType } from '@wix/editor-platform-sdk-types';
import {
  createEditorPlatformManifestServicesError,
  EditorPlatformManifestServicesErrorCode,
} from '../errors';
import {
  ChangeType,
  ManifestChangeDetection,
} from '../ManifestChangeDetection';
import { ManifestComponentExtensionType } from '../stores';
import { ClientSpecMapService } from '../sharedServices';
import type { ManifestServiceFlags } from '../ManifestServiceFlags';

export interface IProcessManifestProps {
  compTypes: string[];
  appDataComponent: ComponentInCsm;
  force?: boolean;
  isPendingApplication?: boolean;
}

export interface IProcessPanelProps {
  compTypes: string[];
  appDataComponent: ComponentInCsm;
  force?: boolean;
}

interface DMEvent {
  type: EventType;
  args: {
    eventPayload: {
      compType: string;
      compRef: CompRef;
      appDefinitionId: string;
    };
  };
}

// TODO: add errors
export class ManifestLifecycle {
  #listeners: {
    manifests: Set<(comp: IProcessManifestProps) => Promise<void>>;
    panels: Set<(comp: IProcessPanelProps) => Promise<void>>;
  } = {
    manifests: new Set(),
    panels: new Set(),
  };
  #onClientSpecMapRefreshCallbacks: Set<() => void | Promise<void>> = new Set();

  constructor(
    private dm: DocumentServicesObject,
    private manifestChangeDetection: ManifestChangeDetection,
    private editorPointerAPI: EditorPointerServiceAPI,
    private manifestServiceFlags: ManifestServiceFlags,
  ) {}

  #manifestLoadingState: Map<string, 'loading' | 'finished'> = new Map();

  startListeners() {
    void this.#processComponents(this.#getInstalledComponents());
    // load manifests for installed components
    this.#startListeners();
  }

  #startListeners() {
    this.dm.platform.subscribeOnAppEvents(this.#eventHandler);
    this.dm.platform.subscribeOnCustomAppEvents(this.#eventHandler);

    this.dm.platform.registerClientSpecMapRefreshCallback?.(
      this.#handleClientSpecMapRefresh.bind(this),
    );
  }

  #parseDMEvent<
    T extends {
      args?: { eventType?: string; eventPayload?: any };
    },
  >(event: DMEvent | string | T): T | null {
    if (typeof event === 'string') {
      try {
        return JSON.parse(event) as T;
      } catch (e) {
        // TODO: unify log/want messages
        console.warn('Failed to parse event string:', event, e);
        return null;
      }
    }
    return event as T;
  }

  #eventHandler = (event: DMEvent | string) => {
    const parsedEvent = this.#parseDMEvent(event);

    if (!parsedEvent || !parsedEvent.args || !parsedEvent.args.eventType) {
      return;
    }

    const { eventType, eventPayload = {} } = parsedEvent.args;

    switch (eventType) {
      case EventType.appInstalled:
        void this.#handleAppInstalledEvent(eventPayload);
        break;

      case EventType.componentAddedToStage:
        void this.#handleComponentAddedEvent(eventPayload);
        break;
      case EventType.widgetAdded:
        void this.#handleWidgetAddedEvent(eventPayload);
        break;

      case EventType.appUpdateCompleted:
        void this.#handleAppInstalledEvent(eventPayload, {
          force: true,
        });
        break;

      default:
        break;
    }
  };

  async #processComponents(
    components: ComponentInCsm[],
    options: { isPendingApplication?: boolean } = {},
  ) {
    await this.#triggerManifestLoad(
      components.map((appDataComponent) => {
        return {
          appDataComponent,
          isPendingApplication: options.isPendingApplication,
        };
      }),
    );

    await this.#triggerPanelLoad(
      components.map((appDataComponent) => {
        return {
          appDataComponent,
          isPending: options.isPendingApplication,
        };
      }),
    );
  }

  async #handleClientSpecMapRefresh() {
    await Promise.allSettled(
      [...this.#onClientSpecMapRefreshCallbacks].map((cb) => cb()),
    );

    void this.#processComponents(this.#getInstalledComponents());
    void this.#processComponents(this.#getPendingComponents(), {
      isPendingApplication: true,
    });
  }

  async loadPendingManifests() {
    await this.#processComponents(this.#getPendingComponents(), {
      isPendingApplication: true,
    });
  }

  async #handleAppInstalledEvent(
    payload: { appDefinitionId?: string },
    options: {
      force?: boolean;
    } = {},
  ) {
    const appDefinitionId = payload.appDefinitionId;
    if (!appDefinitionId) {
      return;
    }

    const appData = this.dm.platform.getAppDataByAppDefId(
      appDefinitionId,
    ) as unknown as EditorClientSpecMapEntry;

    const appDataComponents = appData?.components;

    await this.#triggerManifestLoad(
      (appDataComponents ?? []).map((appDataComponent) => {
        return {
          appDataComponent,
          force: options.force,
        };
      }),
    );

    await this.#triggerPanelLoad(
      (appDataComponents ?? []).map((appDataComponent) => {
        return {
          appDataComponent,
          force: options.force,
        };
      }),
    );
  }

  async #handleComponentAddedEvent(payload: {
    compType?: string;
    componentType?: string;
    compRef: CompRef;
  }) {
    await this.dm.waitForChangesAppliedAsync();

    /**
     * Trigger cache rebuild after components were added potentially in non-blocking manner.
     */
    this.manifestChangeDetection.notify({
      editorPointer: this.editorPointerAPI.buildEditorPointer(payload.compRef),
      type: ChangeType.Invalidate,
      value: null,
      extensionType: ManifestComponentExtensionType.BUILDER,
    });
  }

  async #handleWidgetAddedEvent(payload: {
    componentRef: CompRef;
    widgetId: string;
  }) {
    await this.dm.waitForChangesAppliedAsync();

    /**
     * Trigger cache rebuild after components were added potentially in non-blocking manner.
     */
    this.manifestChangeDetection.notify({
      editorPointer: this.editorPointerAPI.buildEditorPointer(
        payload.componentRef,
      ),
      type: ChangeType.Invalidate,
      value: null,
      extensionType: ManifestComponentExtensionType.OOI,
    });
  }

  async #triggerManifestLoad(
    comps: Omit<IProcessManifestProps, 'compTypes'>[],
  ) {
    const results = await Promise.allSettled(
      comps.flatMap((comp) =>
        [...this.#listeners.manifests].map(async (cb) => {
          const compTypes = ClientSpecMapService.extractCompTypesFromComponent(
            comp.appDataComponent,
            this.manifestServiceFlags,
          );

          compTypes.forEach((compType) => {
            this.#manifestLoadingState.set(compType, 'loading');
          });

          try {
            await cb({
              ...comp,
              compTypes,
            });

            compTypes.forEach((compType) => {
              this.#manifestLoadingState.set(compType, 'finished');
            });
          } catch (e) {
            compTypes.forEach((compType) => {
              this.#manifestLoadingState.delete(compType);
            });

            throw e;
          }
        }),
      ),
    );

    const errorComponents = results
      .map((result) => {
        if (result.status === 'rejected') {
          return result.reason;
        } else {
          return null;
        }
      })
      .filter((x) => !!x);

    if (errorComponents.length) {
      console.error(
        createEditorPlatformManifestServicesError(
          EditorPlatformManifestServicesErrorCode.LoadComponentPanelError,
          'some components failed to load',
        ).withMultipleErrors(errorComponents),
      );
    }
  }

  onManifestLoad(cb: (comp: IProcessManifestProps) => Promise<void>) {
    this.#listeners.manifests.add(cb);
  }

  async #triggerPanelLoad(comps: Omit<IProcessPanelProps, 'compTypes'>[]) {
    const results = await Promise.allSettled(
      comps.flatMap((comp) =>
        [...this.#listeners.panels].map((cb) => {
          return cb({
            ...comp,
            compTypes: ClientSpecMapService.extractCompTypesFromComponent(
              comp.appDataComponent,
              this.manifestServiceFlags,
            ),
          });
        }),
      ),
    );

    const errorComponents = results
      .map((result) => {
        if (result.status === 'rejected') {
          return result.reason;
        } else {
          return null;
        }
      })
      .filter((x) => !!x);

    if (errorComponents.length) {
      console.error(
        createEditorPlatformManifestServicesError(
          EditorPlatformManifestServicesErrorCode.LoadComponentPanelError,
          'some components failed to load',
        ).withMultipleErrors(errorComponents),
      );
    }
  }

  onPanelLoad(cb: (comp: IProcessPanelProps) => Promise<void>) {
    this.#listeners.panels.add(cb);
  }

  onClientSpecMapRefresh(cb: () => void | Promise<void>): () => void {
    this.#onClientSpecMapRefreshCallbacks.add(cb);
    return () => {
      this.#onClientSpecMapRefreshCallbacks.delete(cb);
    };
  }

  async handleApp(appData: EditorClientSpecMapEntry) {
    await this.#triggerManifestLoad(
      this.#prepareAppComponents(appData).map((appDataComponent) => ({
        appDataComponent,
        force: true,
      })),
    );

    await this.#triggerPanelLoad(
      (appData.components ?? []).map((appDataComponent) => {
        return {
          appDataComponent,
          force: true,
        };
      }),
    );
  }

  #prepareAppComponents(appData: EditorClientSpecMapEntry): ComponentInCsm[] {
    return (appData.components ?? []).map((compData) => {
      if (!compData.componentId) {
        return compData;
      }
      const widgetData = appData.widgets?.[compData.componentId];
      if (!widgetData) {
        return compData;
      }
      // @ts-expect-error builderConfigUrl needs to be added
      const builderConfigUrl = widgetData.componentFields?.builderConfigUrl;
      if (!builderConfigUrl) {
        return compData;
      }
      const result: ComponentInCsm = {
        ...compData,
        data: {
          ...compData.data,
          // @ts-expect-error builderConfigUrl needs to be added
          builderConfigUrl,
        },
      };
      return result;
    });
  }

  #getInstalledComponents() {
    const installedApps = this.dm.platform.getInstalledApps();

    return installedApps.flatMap(
      (app) => app.components ?? [],
      // DM types mismatch!!!
    ) as ComponentInCsm[];
  }

  #getPendingComponents() {
    const pendingApps = this.dm.tpa.getPendingApps();

    return pendingApps.flatMap(
      (app) => app.components ?? [],
      // DM types mismatch!!!
    ) as ComponentInCsm[];
  }

  isManifestLoading(compType: string) {
    return this.#manifestLoadingState.get(compType) === 'loading';
  }
}
